import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Search, User, Activity, TrendingUp, TrendingDown,
  Clock, AlertTriangle, ChevronRight, Apple,
  Droplet, Target, UserPlus, Mail, Users, Salad,
  UserCheck, UserX, UserMinus, PlayCircle, PauseCircle, XCircle, ShoppingCart
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import InvitePatient from "@/components/InvitePatient";
import AssignMealPlan from "@/components/AssignMealPlan";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DietitianPatients = () => {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMealPlanModal, setShowMealPlanModal] = useState(false);
  const [selectedPatientForMealPlan, setSelectedPatientForMealPlan] = useState(null);
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [activeStatusTab, setActiveStatusTab] = useState("all");
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [complianceFilter, setComplianceFilter] = useState(null);

  const STATUS_CONFIG = {
    enrolled: { label: t('physician.enrolled'), color: "bg-blue-100 text-blue-700", icon: UserPlus },
    ongoing: { label: t('physician.ongoing_treatment'), color: "bg-green-100 text-green-700", icon: PlayCircle },
    suspended: { label: t('physician.suspended'), color: "bg-yellow-100 text-yellow-700", icon: PauseCircle },
    discharged: { label: t('physician.discharged'), color: "bg-gray-100 text-gray-600", icon: XCircle },
  };

  useEffect(() => {
    fetchPatients();
    fetchLicenseStatus();
    fetchPendingInvitations();
  }, [token]);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/dietitian/patients`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setPatients(data.patients || []);
      } else {
        toast.error("Failed to load patients");
      }
    } catch (error) {
      console.error("Fetch patients error:", error);
      toast.error("Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

  const fetchLicenseStatus = async () => {
    try {
      const response = await fetch(`${API}/invitations/license-status`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setLicenseStatus(data);
      }
    } catch (error) {
      console.error("Fetch license status error:", error);
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      const response = await fetch(`${API}/invitations/pending`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingInvitations(data.invitations || []);
      }
    } catch (error) {
      console.error("Fetch pending invitations error:", error);
    }
  };

  const handleInviteSent = () => {
    fetchLicenseStatus();
    fetchPendingInvitations();
  };

  const updatePatientStatus = async (patientId, newStatus) => {
    setUpdatingStatus(patientId);
    try {
      const response = await fetch(`${API}/dietitian/patient/${patientId}/status`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Patient status updated to ${STATUS_CONFIG[newStatus].label}`);
        // Update local state
        setPatients(prev => prev.map(p => 
          p.user_id === patientId ? { ...p, status: newStatus } : p
        ));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update status");
      }
    } catch (error) {
      console.error("Update status error:", error);
      toast.error("Failed to update patient status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getComplianceBadge = (score) => {
    if (score >= 71) return { label: t('physician.good'), color: "bg-success/10 text-success" };
    if (score >= 41) return { label: t('physician.moderate'), color: "bg-warning/10 text-warning" };
    return { label: t('physician.needs_improvement'), color: "bg-destructive/10 text-destructive" };
  };

  const getRiskBadge = (avgCarbs) => {
    if (avgCarbs <= 45) return { label: "Low Risk", color: "text-success" };
    if (avgCarbs <= 60) return { label: "Moderate", color: "text-warning" };
    return { label: "High Risk", color: "text-destructive" };
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return "Never";
    const now = new Date();
    const then = new Date(timestamp);
    const diffHours = Math.floor((now - then) / (1000 * 60 * 60));
    
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "Yesterday";
    return `${diffDays} days ago`;
  };

  const filteredPatients = patients.filter((p) => {
    const matchesSearch = 
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = activeStatusTab === "all" || 
      (p.status || "enrolled") === activeStatusTab;
    
    let matchesCompliance = true;
    if (complianceFilter === "good") matchesCompliance = p.compliance_score >= 71;
    else if (complianceFilter === "moderate") matchesCompliance = p.compliance_score >= 41 && p.compliance_score < 71;
    else if (complianceFilter === "poor") matchesCompliance = p.compliance_score < 41;
    else if (complianceFilter === "hba1c") matchesCompliance = !!p.estimated_hba1c;
    
    return matchesSearch && matchesStatus && matchesCompliance;
  });

  // Stats by status
  const statusCounts = {
    all: patients.length,
    enrolled: patients.filter(p => (p.status || "enrolled") === "enrolled").length,
    ongoing: patients.filter(p => p.status === "ongoing").length,
    suspended: patients.filter(p => p.status === "suspended").length,
    discharged: patients.filter(p => p.status === "discharged").length,
  };

  // Stats
  const totalPatients = patients.length;
  const goodCompliance = patients.filter(p => p.compliance_score >= 71).length;
  const moderateCompliance = patients.filter(p => p.compliance_score >= 41 && p.compliance_score < 71).length;
  const poorCompliance = patients.filter(p => p.compliance_score < 41).length;
  const patientsWithHba1c = patients.filter(p => p.estimated_hba1c);
  const avgHba1c = patientsWithHba1c.length > 0 
    ? (patientsWithHba1c.reduce((sum, p) => sum + p.estimated_hba1c, 0) / patientsWithHba1c.length).toFixed(1)
    : "N/A";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Invite Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary">{t('physician.my_patients')}</h1>
          <p className="text-muted-foreground">{t('physician.manage_patients')}</p>
        </div>
        <Button 
          onClick={() => setShowInviteModal(true)}
          data-testid="invite-patient-btn"
          className="bg-orange-500 hover:bg-orange-600 gap-2"
          disabled={licenseStatus && !licenseStatus.can_invite}
        >
          <UserPlus className="w-4 h-4" />
          {t('physician.invite_patient')}
        </Button>
      </div>

      {/* License Status Banner */}
      {licenseStatus && (
        <Card className="p-4 rounded-2xl border-blue-100 bg-blue-50/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-secondary">{t('physician.patient_slots')}</p>
                <p className="text-sm text-muted-foreground">
                  {licenseStatus.used_licenses} / {licenseStatus.total_licenses} {t('admin.used')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">{licenseStatus.remaining_licenses}</p>
                <p className="text-xs text-muted-foreground">{t('admin.available')}</p>
              </div>
              {pendingInvitations.length > 0 && (
                <div className="text-right pl-4 border-l">
                  <p className="text-lg font-bold text-orange-500">{pendingInvitations.length}</p>
                  <p className="text-xs text-muted-foreground">{t('physician.pending')}</p>
                </div>
              )}
              {/* Buy Licenses - only for private practice physicians */}
              {(user?.is_private_physician || !user?.partner_id) && (
                <Button
                  onClick={() => window.dispatchEvent(new CustomEvent('openLicensePurchase'))}
                  className="rounded-xl bg-primary text-white hover:bg-primary/90"
                  data-testid="buy-licenses-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {t('physician.buy_licenses')}
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <Card className="p-4 rounded-2xl border-orange-100">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-orange-500" />
            <h3 className="font-medium text-secondary">{t('physician.pending_invitations')}</h3>
          </div>
          <div className="space-y-2">
            {pendingInvitations.slice(0, 3).map((inv) => (
              <div key={inv.invitation_id} className="flex items-center justify-between py-2 px-3 bg-orange-50/50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{inv.patient_name}</p>
                  <p className="text-xs text-muted-foreground">{inv.patient_email}</p>
                </div>
                <code className="text-xs bg-white px-2 py-1 rounded font-mono">{inv.invite_code}</code>
              </div>
            ))}
            {pendingInvitations.length > 3 && (
              <p className="text-xs text-muted-foreground text-center">
                {t('physician.more_pending').replace('{count}', pendingInvitations.length - 3)}
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Stats Cards - Clickable to filter */}
      {complianceFilter && (
        <div className="flex items-center gap-2">
          <Badge className="bg-primary/10 text-primary">
            Filtered: {complianceFilter === "good" ? "Good Compliance" : complianceFilter === "moderate" ? "Moderate" : complianceFilter === "poor" ? "Needs Attention" : complianceFilter === "hba1c" ? "With HbA1c Data" : "All"} ({filteredPatients.length})
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => setComplianceFilter(null)} className="text-xs h-6">Clear</Button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-4">
        <Card 
          className={`p-4 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md ${complianceFilter === null ? 'ring-2 ring-primary/20' : ''}`}
          onClick={() => setComplianceFilter(complianceFilter === null ? null : null)}
          data-testid="stat-total-patients"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">{totalPatients}</p>
              <p className="text-xs text-muted-foreground">{t('physician.total_patients')}</p>
            </div>
          </div>
        </Card>

        <Card 
          className={`p-4 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md ${complianceFilter === 'good' ? 'ring-2 ring-success' : ''}`}
          onClick={() => setComplianceFilter(complianceFilter === 'good' ? null : 'good')}
          data-testid="stat-good-compliance"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold text-success">{goodCompliance}</p>
              <p className="text-xs text-muted-foreground">{t('physician.good_compliance')}</p>
            </div>
          </div>
        </Card>

        <Card 
          className={`p-4 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md ${complianceFilter === 'moderate' ? 'ring-2 ring-warning' : ''}`}
          onClick={() => setComplianceFilter(complianceFilter === 'moderate' ? null : 'moderate')}
          data-testid="stat-moderate-compliance"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center">
              <Activity className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-bold text-warning">{moderateCompliance}</p>
              <p className="text-xs text-muted-foreground">{t('physician.moderate')}</p>
            </div>
          </div>
        </Card>

        <Card 
          className={`p-4 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md ${complianceFilter === 'poor' ? 'ring-2 ring-destructive' : ''}`}
          onClick={() => setComplianceFilter(complianceFilter === 'poor' ? null : 'poor')}
          data-testid="stat-needs-attention"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="text-2xl font-bold text-destructive">{poorCompliance}</p>
              <p className="text-xs text-muted-foreground">{t('physician.needs_attention')}</p>
            </div>
          </div>
        </Card>

        <Card 
          className={`p-4 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md ${complianceFilter === 'hba1c' ? 'ring-2 ring-purple-500' : ''}`}
          onClick={() => setComplianceFilter(complianceFilter === 'hba1c' ? null : 'hba1c')}
          data-testid="stat-avg-hba1c"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{avgHba1c}%</p>
              <p className="text-xs text-muted-foreground">{t('physician.avg_hba1c')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          type="text"
          placeholder={t('physician.search_patients')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 rounded-xl border-orange-100"
          data-testid="patient-search"
        />
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "all", label: t('physician.all_patients'), count: statusCounts.all },
          { key: "enrolled", label: t('physician.enrolled'), count: statusCounts.enrolled },
          { key: "ongoing", label: t('physician.ongoing_treatment'), count: statusCounts.ongoing },
          { key: "suspended", label: t('physician.suspended'), count: statusCounts.suspended },
          { key: "discharged", label: t('physician.discharged'), count: statusCounts.discharged },
        ].map((tab) => (
          <Button
            key={tab.key}
            variant={activeStatusTab === tab.key ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveStatusTab(tab.key)}
            className={`rounded-full ${activeStatusTab === tab.key ? "bg-primary text-white" : "border-orange-200"}`}
            data-testid={`status-tab-${tab.key}`}
          >
            {tab.label} ({tab.count})
          </Button>
        ))}
      </div>

      {/* Patients Table */}
      <Card className="rounded-2xl border-orange-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-50/50">
              <tr>
                <th className="text-left p-4 font-medium text-secondary">{t('physician.patient')}</th>
                <th className="text-left p-4 font-medium text-secondary">{t('physician.status')}</th>
                <th className="text-left p-4 font-medium text-secondary hidden md:table-cell">{t('physician.last_meal')}</th>
                <th className="text-left p-4 font-medium text-secondary hidden lg:table-cell">{t('physician.avg_carbs')}</th>
                <th className="text-left p-4 font-medium text-secondary">{t('physician.compliance')}</th>
                <th className="text-left p-4 font-medium text-secondary hidden lg:table-cell">{t('physician.est_hba1c')}</th>
                <th className="text-right p-4 font-medium text-secondary">{t('physician.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    {patients.length === 0 
                      ? t('physician.no_patients_yet')
                      : t('physician.no_patients_search')}
                  </td>
                </tr>
              ) : (
                filteredPatients.map((patient) => {
                  const compliance = getComplianceBadge(patient.compliance_score || 0);
                  const patientStatus = patient.status || "enrolled";
                  const statusConfig = STATUS_CONFIG[patientStatus];
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr key={patient.user_id} className="border-t border-orange-100 hover:bg-blue-50/30">
                      <td className="p-3 sm:p-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-secondary text-sm truncate">{patient.name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground truncate">{patient.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="p-3 sm:p-4 hidden md:table-cell">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          {getTimeAgo(patient.last_meal)}
                        </div>
                      </td>
                      <td className="p-3 sm:p-4 hidden lg:table-cell">
                        <div className="flex items-center gap-2">
                          <Apple className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{patient.avg_carbs_7d || 0}g</span>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4">
                        <span className={`px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium ${compliance.color}`}>
                          {patient.compliance_score || 0}%
                        </span>
                      </td>
                      <td className="p-3 sm:p-4 hidden lg:table-cell">
                        <div className="flex items-center gap-2">
                          <Droplet className="w-4 h-4 text-purple-500" />
                          <span className="font-medium">
                            {patient.estimated_hba1c ? `${patient.estimated_hba1c}%` : "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="rounded-full text-green-600 hover:bg-green-50"
                            onClick={() => {
                              setSelectedPatientForMealPlan({
                                patient_id: patient.user_id,
                                name: patient.name,
                                email: patient.email
                              });
                              setShowMealPlanModal(true);
                            }}
                            data-testid={`assign-meal-plan-${patient.user_id}`}
                          >
                            <Salad className="w-4 h-4" />
                          </Button>
                          
                          {/* Status Change Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="rounded-full"
                                disabled={updatingStatus === patient.user_id}
                              >
                                {updatingStatus === patient.user_id ? "..." : t('physician.status')}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {patientStatus !== "enrolled" && (
                                <DropdownMenuItem onClick={() => updatePatientStatus(patient.user_id, "enrolled")}>
                                  <UserPlus className="w-4 h-4 mr-2 text-blue-600" />
                                  {t('physician.set_enrolled')}
                                </DropdownMenuItem>
                              )}
                              {patientStatus !== "ongoing" && (
                                <DropdownMenuItem onClick={() => updatePatientStatus(patient.user_id, "ongoing")}>
                                  <PlayCircle className="w-4 h-4 mr-2 text-green-600" />
                                  {t('physician.start_treatment')}
                                </DropdownMenuItem>
                              )}
                              {patientStatus !== "suspended" && (
                                <DropdownMenuItem onClick={() => updatePatientStatus(patient.user_id, "suspended")}>
                                  <PauseCircle className="w-4 h-4 mr-2 text-yellow-600" />
                                  {t('physician.suspend')}
                                </DropdownMenuItem>
                              )}
                              {patientStatus !== "discharged" && (
                                <DropdownMenuItem onClick={() => updatePatientStatus(patient.user_id, "discharged")}>
                                  <XCircle className="w-4 h-4 mr-2 text-gray-600" />
                                  {t('physician.discharge')}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          
                          <Link to={`/physician/patient/${patient.user_id}`}>
                            <Button variant="ghost" size="sm" className="rounded-full text-blue-600 hover:bg-blue-50">
                              {t('physician.view_profile')}
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Flagged Patients Alert */}
      {poorCompliance > 0 && (
        <Card className="p-4 rounded-2xl border-destructive/20 bg-destructive/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive mt-0.5" />
            <div>
              <p className="font-medium text-destructive">{t('physician.patients_needing_attention')}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('physician.poor_compliance_alert').replace('{count}', poorCompliance)}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Invite Patient Modal */}
      <InvitePatient 
        open={showInviteModal} 
        onClose={() => setShowInviteModal(false)}
        onInviteSent={handleInviteSent}
      />

      {/* Assign Meal Plan Modal */}
      <AssignMealPlan
        open={showMealPlanModal}
        onClose={() => {
          setShowMealPlanModal(false);
          setSelectedPatientForMealPlan(null);
        }}
        patient={selectedPatientForMealPlan}
        onAssigned={() => fetchPatients()}
      />
    </div>
  );
};

export default DietitianPatients;
